import moment from 'moment';
import ShelterlyPDF from '../utils/pdf';
import { capitalize } from '../utils/formatString';
import { buildAnimalCareScheduleDoc, buildAnimalCountList } from '../animals/Utils';
import {
  DATE_FORMAT,
} from "../constants";

/**
 * builds owner summary content
 *
 * @param {ShelterlyPDF} pdf
 * @param {Array} owners
 * @param {Array} [animalsOverride] optionally set the animals, otherwise will use animals within the owner object
 * @returns {ShelterlyPDF}
 */
const buildOwnersContent = (pdf, owners, organization, animalsOverride) => {
  const groupedPageCounts = [];
  let ownerPageCount = 1;
  owners.forEach((owner, i) => {
    if (i > 0) {
      pdf.drawPageBreak();
      pdf.drawPageHeader();
      
      // before resetting the owner page count, store the current count
      groupedPageCounts.push(ownerPageCount);

      // reset the page count for each owner
      ownerPageCount = 1;
    }

    const ownerInfoList = [
      `Name: ${owner.first_name} ${owner.last_name}`,
      `Owner ID: #${owner.id || 'N/A'}`,
      `Agency: ${owner.agency || 'N/A'}`,
      `Telephone: ${owner.display_phone || 'N/A'} ${owner.display_alt_phone ? `  Alt: ${owner.display_alt_phone}` : ''}`,
      `Email: ${owner.email || 'N/A'}`,
      `Drivers License: ${owner.drivers_license || 'N/A'}`
    ];

    if (owner.address) {
        ownerInfoList.push(`Address: ${owner.full_address}`);
    } else {
      ownerInfoList.push('Address: N/A');
    }

    if (owner.requests){
      ownerInfoList.push('Service Requests:');
      owner.requests.forEach((request, i) => {
        ownerInfoList.push(`SR#${request.id_for_incident} - ${request.full_address}`)
      })
    }

    pdf.drawTextList({
      labels: ownerInfoList,
      bottomPadding: 15
    });

    // Owner Comments
    pdf.drawWrappedText({
      text: `Comments: ${owner.comments || 'N/A'}`
    });

    pdf.drawHRule();

    function drawAnimalHeader() {
      pdf.drawSectionHeader({ text: 'Animals', hRule: false });
    }

    const animals = animalsOverride || owner?.animals || [];

    drawAnimalHeader();

    buildAnimalCountList(pdf, animals);

    pdf.drawHRule();

    // keep the last y position after a row was drawn
    let lastYPosAfterDraw = pdf.getLastYPositionWithBuffer({ buffer: 0 });

    animals.forEach((animal, animalIndex) => {
      // grab the last y position before we draw a row
      let lastYPosBeforeDraw = pdf.getLastYPositionWithBuffer({ buffer: 0 });
      
      // look ahead to see if this animal will bleed to the next page
      const estimatedAnimalHeight = 162 // this is a magic number, it should change if we add or remove properties to render for animals
      if (pdf.remainderPageHeight <= estimatedAnimalHeight) {
        pdf.drawPageBreak();
        lastYPosBeforeDraw = pdf.getLastYPositionWithBuffer({ buffer: 0 });
        ownerPageCount++;
      }

      const animalInfoList = [
        `ID: A#${animal.id_for_incident}`,
        `Status: ${capitalize(animal.status.toLowerCase(), { proper: true })}`,
        `Name: ${animal.name || 'Unknown Name'}`,
        `Species: ${capitalize(animal.species_string)}`,
        `Sex: ${capitalize(animal.sex|| 'Unknown')}`,
        `Age: ${capitalize(animal.age || 'Unknown')}`,
        `Size: ${capitalize(animal.size || 'Unknown')}`,
        `Primary Color: ${capitalize(animal.pcolor || 'N/A')}, Secondary Color: ${capitalize(animal.scolor || 'N/A')}`
      ];

      pdf.drawTextList({
        labels: animalInfoList,
        listStyle: 'grid',
        bottomPadding: 0
      });

      pdf.drawPad();

      pdf.drawWrappedText({
        text: `Breed / Description: ${animal.color_notes || 'N/A'}`,
        linePadding: -5
      });

      pdf.drawWrappedText({
        text: `Shelter Address: ${animal.shelter_object?.full_address || 'N/A'}`,
        linePadding: 5
      });

      pdf.drawPad(5);
      pdf.drawHRule();
      lastYPosAfterDraw = pdf.getLastYPositionWithBuffer();

      // If after draw y position is less than before draw, that means there was a page break.
      // Draw the animal header again.
      if (
          lastYPosAfterDraw < lastYPosBeforeDraw &&
          animalIndex < animals.length - 1
        ) {
        drawAnimalHeader();
      }
    });

    pdf.drawPageBreak();
    ownerPageCount++;

    // Draw liability form
    pdf = buildVetRelease(pdf, organization);
    pdf.drawPageBreak();
    ownerPageCount++;


    pdf = buildLiabiltyRelease(pdf, organization);
    pdf = buildAnimalRelease(pdf, organization);

  });

  // push the last owner page count
  groupedPageCounts.push(ownerPageCount);

  const totalPageCount = pdf.numberOfPages
  let ownerPageNumber = 1
  let groupIndex = 0;

  // draw the page numbers
  for (let pageNumber = 1; pageNumber <= totalPageCount; pageNumber++) {
    pdf.setPage(pageNumber);

    let ownerPageCount = groupedPageCounts[groupIndex];
    if (ownerPageNumber > ownerPageCount) {
      ownerPageNumber = 1
      groupIndex++;
      ownerPageCount = groupedPageCounts[groupIndex];
    }
    pdf.drawPageNumbers({
      pageNumber: ownerPageNumber,
      pageCount: ownerPageCount
    });
    ownerPageNumber++;
  }

  return pdf;
}

const buildOwnersDoc = (owners, organization) => {
  const pdf = new ShelterlyPDF({}, {
    pageTitle: 'Owner Summary/Liability',
    pageSubtitle: `Date: ${new Date().toLocaleDateString()}`,
  });

  return buildOwnersContent(pdf, owners, organization);
};

function printOwnerDetails(owner = {}, organization = {}) {
  const pdf = buildOwnersDoc([owner], organization);
  pdf.fileName = `Owner-Summary-${owner.id.toString().padStart(3, 0)}`;
  return pdf.saveFile();
}

async function printAllOwnersDetails(owners = [], organization = {}) {
  const pdf = buildOwnersDoc(owners, organization);
  pdf.fileName = `Owner-Summaries-${moment().format(DATE_FORMAT)}`;
  return pdf.saveFile();
}

const printOwnerAnimalCareSchedules  = async (animals = [], ownerId = 0) => {
  // sort animals by id
  const sortedAnimals = [...animals].sort((a,b) => a.id - b.id);

  const pdf = await buildAnimalCareScheduleDoc(sortedAnimals);
  pdf.fileName = `Shelterly-Owner-Animal-Care-Schedules-${ownerId.toString().padStart(4, 0)}-${moment().format(DATE_FORMAT)}`;
  return pdf.saveFile();
};

function buildLiabiltyRelease(pdf, organization) {
  pdf.drawSectionHeader({
    text: "Liability Release",
    hRule: false,
    align: ShelterlyPDF.AlignTypes.CENTER,
  });
  pdf.drawPad(18);
  pdf.drawHRule({ buffer: 3});
  let liabilityPreface = `Due to a declared emergency, I am requesting ${organization.liability_name} to board my animal(s) `;
  liabilityPreface += '(listed above) and agree to all of the following:';
  pdf.setDocumentFontSize({ size: 8 });
  pdf.drawWrappedText({ text: liabilityPreface, linePadding: -2 });
  pdf.setDocumentFontSize();

  function drawAgreements(agreements) {
    agreements.forEach((agreement, i) => {
      pdf.drawWrappedText({ text: `${i + 1})    ${agreement}`, linePadding: -3, bottomPadding: 5 })
    });
  }

  const agreements = [
    // 1)
    'I understand that my animal(s) may be exposed to disease and other risks while being ' +
    'housed at the shelter and other facilities and therefore I will not hold ' +
    `${organization.liability_name} responsible for the health or death of my animal(s).`,

    // 2)
    'I agree to attempt to find alternate housing for my animal(s) as soon as possible.',

    // 3)
    `I agree to contact the agency on a regular basis to keep ${organization.liability_name} ` +
    'updated on my whereabouts and possible alternate housing. ' +
    'Emergency Contact ____________________',

    // 4)
    'I understand that this boarding agreement is temporary and I agree to make arrangements ' +
    'for or claim my animal(s) at the close of the shelter.',

    // 5)
    'I understand that I may be subject to boarding fees at the close of the shelter.',

    // 6)
    'I understand that photographs of myself and my animal(s) may be taken.'
  ];

  drawAgreements(agreements);

  const releaseLabels = [
    "I Allow",
    { label: "or", type: "text" },
    "I Decline",
    {
      label:
        "any photos that are taken to be released to the media or public view",
      type: "text",
    }
  ].concat(Array(7).fill({
    label: ' ',
    type: 'text'
  }));
  pdf.drawCheckboxList({
    labels: releaseLabels,
    listStyle: 'inline',
  });
  pdf = drawSignatureLines(pdf, organization);
  return pdf;
}
function buildAnimalRelease(pdf, organization) {
  pdf.drawSectionHeader({
    text: "Release of Animal(s)",
    hRule: false,
    align: ShelterlyPDF.AlignTypes.CENTER,
  });
  pdf.drawPad(18);
  pdf.drawHRule({ buffer: 3});

  let releaseText = 'I hereby acknowledge that I am the owner/responsible person for the above animal(s). I have taken ';
  releaseText += 'custody of my animal(s) and am now responsible for their care and transportation.';
  pdf.drawWrappedText({ text: releaseText, linePadding: -2 });
  pdf = drawSignatureLines(pdf, organization);
  return pdf;
}

function buildVetRelease(pdf, organization){
    pdf.drawSectionHeader({
      text: "Vet Liability Release",
      hRule: false,
      align: ShelterlyPDF.AlignTypes.CENTER,
    });
    pdf.drawPad(18);
    pdf.drawHRule({ buffer: 3});

    let vetMicrochipText = "______ (initial) Veterinary Care/Microchip: I, the undersigned, understand that my animal(s) may be exposed to "
    vetMicrochipText += "diseases and other risks while being sheltered and that my animal(s) will be provided veterinary care for injury/illness, "
    vetMicrochipText += "wellness, population health, and/or microchipping in alignment with the local jurisdiction's animal intake protocols. I "
    vetMicrochipText += "understand that there are risks associated with vaccinations, medications, and general medical procedures and that the "
    vetMicrochipText += "veterinarians will take every reasonable precaution to minimize these risks."

    pdf.drawWrappedText({ text: vetMicrochipText, linePadding: -2})

    let vetCPRText = "______ (initial) I ( do ) / ( do not ) authorize Cardiac Pulmonary Resuscitation (CPR): In the event that my animal(s) goes "
    vetCPRText += "into cardiac or respiratory arrest (no heartbeat and/or breathing), I elect that the veterinary staff, if possible, perform "
    vetCPRText += "CPR including administration of chest compressions, endotracheal intubation, manual breathing, and IV emergency medications to "
    vetCPRText += "try and restart the heart. I understand that the success rate of CPR in patients in cardiac or respiratory arrest is < 5%."

    pdf.drawWrappedText({ text: vetCPRText, linePadding: -2})
    pdf = drawSignatureLines(pdf, organization);
    return pdf;
}

function drawSignatureLines(pdf, organization) {
  pdf.drawTextList({
    labels: ['Owner\'s Signature: ', '  Date: '],
    listStyle: 'grid',
    bottomPadding: 10,
    withLines: true
  });
  pdf.drawTextList({
    labels: [`${organization.liability_short_name} Witness Signature: `],
    bottomPadding: 15,
    withLines: true
  });
  return pdf;
}

export {
  buildOwnersContent,
  printOwnerDetails,
  printAllOwnersDetails,
  printOwnerAnimalCareSchedules,
  buildVetRelease,
  buildLiabiltyRelease,
  buildAnimalRelease
}
